/**
 * React Query hooks for the mandate, library and proposal endpoints.
 *
 * Every call goes through `useApiClient`, so with VITE_USE_MOCK_API=true it is
 * answered by src/mandate/mockApi.ts and otherwise it goes to the real backend at
 * the same paths (see API_CONTRACT.md). Nothing in this file knows which.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiClientError, useApiClient } from '../api/client';
import { useApiQuery } from '../api/useApiQuery';
import type {
  MandateAccountContext,
  MandateBody,
  MandatePortfolioProvisioning,
  MandateRecord,
  ValidationErrorBody,
  Violation,
} from './types';
import type { LibraryResponse } from './library';
import type { CellsResponse, FamiliesResponse } from './cells';
import type { AlternativeRejected, ApproveResponse, Plan, Proposal } from './proposal';

export const mandateKeys = {
  context: ['mandate', 'context'] as const,
  active: ['mandate', 'active'] as const,
  history: ['mandate', 'history'] as const,
  library: (forActive: boolean) => ['library', forActive ? 'active-mandate' : 'all'] as const,
  libraryCells: ['library', 'cells'] as const,
  libraryFamilies: ['library', 'families'] as const,
  proposals: ['proposals'] as const,
  latestProposal: ['proposals', 'latest'] as const,
};

export function useAccountContext() {
  return useApiQuery<{ accounts: MandateAccountContext[] }>(mandateKeys.context, '/api/mandates/context');
}

/**
 * `enabled` (default true) lets a caller that only needs this to decide a
 * redirect -- see AppShell's mandate-first entry guard -- skip the request
 * entirely when the mandate UI itself is off, instead of firing a query
 * against an endpoint that may not exist on the real backend yet.
 */
export function useActiveMandate(enabled: boolean = true) {
  return useApiQuery<{ mandate: MandateRecord | null; portfolio_provisioning: MandatePortfolioProvisioning | null }>(
    mandateKeys.active,
    '/api/mandates/active',
    { enabled },
  );
}

export function useMandateHistory() {
  return useApiQuery<{ mandates: MandateRecord[]; total: number }>(mandateKeys.history, '/api/mandates');
}

/** Body of `POST /api/mandates/:id/sign` (server: `SignRequest` / `AckInput`). */
export interface SignRequestBody {
  acknowledgements: Array<{ doc: string; doc_version: string }>;
}

/**
 * The sign request for a mandate body: the documents the user ticked, in the
 * shape the server wants. The server checks the required acknowledgements on
 * the SIGN request (not on the draft's stored `basis.acknowledgements`), so
 * they are sent again here, taken from the same body the draft was created
 * from rather than listed a second time.
 */
export function signRequestFor(body: MandateBody): SignRequestBody {
  return {
    acknowledgements: body.basis.acknowledgements.map((a) => ({ doc: a.doc, doc_version: a.doc_version })),
  };
}

/**
 * Create a new mandate version as a draft, then sign it. Two calls on purpose:
 * the draft is validated and versioned by the server first, and only then does a
 * signature (which activates it and supersedes the old version) happen.
 */
export function useCreateAndSignMandate() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation<MandateRecord, Error, { accountId: string; body: MandateBody }>({
    mutationFn: async ({ accountId, body }) => {
      const draft = await api.post<MandateRecord>('/api/mandates', { account_id: accountId, body });
      return api.post<MandateRecord>(`/api/mandates/${draft.mandate_id}/sign`, signRequestFor(body));
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['mandate'] });
      void qc.invalidateQueries({ queryKey: ['library'] });
      void qc.invalidateQueries({ queryKey: mandateKeys.proposals });
    },
  });
}

/** `forActiveMandate` adds eligibility per entry against the active mandate. */
export function useLibrary(forActiveMandate: boolean) {
  return useApiQuery<LibraryResponse>(
    mandateKeys.library(forActiveMandate),
    forActiveMandate ? '/api/library?for=active-mandate' : '/api/library',
  );
}

/** R41: the pre-registered / evaluated cells. An empty list until a cell is committed. */
export function useLibraryCells() {
  return useApiQuery<CellsResponse>(mandateKeys.libraryCells, '/api/library/cells');
}

/** R41: the per-rule family ledgers with their multiplicity disclosure. An empty list until a cell is committed. */
export function useLibraryFamilies() {
  return useApiQuery<FamiliesResponse>(mandateKeys.libraryFamilies, '/api/library/families');
}

export function useProposals() {
  return useApiQuery<{ proposals: Proposal[]; total: number }>(mandateKeys.proposals, '/api/proposals');
}

/** One proposal by id, with its plan if it was approved; skipped when `id` is null. */
export function useProposal(id: string | null) {
  return useApiQuery<{ proposal: Proposal; plan: Plan | null }>(
    ['proposals', 'by-id', id ?? 'none'],
    `/api/proposals/${id ?? ''}`,
    { enabled: id !== null },
  );
}

export function useLatestProposal() {
  return useApiQuery<{ proposal: Proposal | null; plan: Plan | null }>(
    mandateKeys.latestProposal,
    '/api/proposals/latest',
  );
}

/** Ask the agent to propose a portfolio from the eligible library entries. */
export function useAskAgentForProposal() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation<Proposal, Error, void>({
    mutationFn: () => api.post<Proposal>('/api/proposals', {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: mandateKeys.proposals });
    },
  });
}

export function useApproveProposal(proposalId: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation<ApproveResponse, Error, { overrideNote?: string }>({
    mutationFn: ({ overrideNote }) =>
      api.post<ApproveResponse>(`/api/proposals/${proposalId}/approve`, overrideNote ? { override_note: overrideNote } : {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: mandateKeys.proposals });
    },
  });
}

/** Field-level problems from a 422, or [] when the error carries none. */
export function violationsFromError(err: unknown): Violation[] {
  if (err instanceof ApiClientError) {
    const d = err.details as Partial<ValidationErrorBody> | undefined;
    if (d && Array.isArray(d.violations)) return d.violations;
  }
  return [];
}

/** Machine-readable reason code on an error body (e.g. `mandate_changed`), if any. */
export function errorCode(err: unknown): string | undefined {
  if (err instanceof ApiClientError) {
    const d = err.details as { code?: unknown } | undefined;
    if (d && typeof d.code === 'string') return d.code;
  }
  return undefined;
}

/**
 * The candidates a 409 from `POST /api/proposals` says it rejected (with
 * their reasons), when the body carries them; [] otherwise.
 */
export function rejectedFromError(err: unknown): AlternativeRejected[] {
  if (err instanceof ApiClientError) {
    const d = err.details as { alternatives_rejected?: unknown } | undefined;
    if (d && Array.isArray(d.alternatives_rejected)) return d.alternatives_rejected as AlternativeRejected[];
  }
  return [];
}
